import { Checkbox, type CheckboxChangeEventType, Input } from '@myosh/odin-components';
import clsx from 'clsx';
import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../../redux/hooks';
import { useGetLocalesQuery } from '../../../../redux/services/api';
import { useHierarchyTypesQuery } from '../../../../redux/services/hierarchy';
import {
  type FormBuilerHierarchyType,
  selectHierarchyTypes,
  selectSelectedHierarchy,
  setSelectedHierarchy,
  updateHierarchyFieldData,
} from '../../../../redux/slices/form-builder-slice';
import { forceAssert } from '../../../../utils/type-guards';
import { mergeHierarchyFields } from './hierarchy-field.util';

type FormValues = Record<string, FormBuilerHierarchyType>;

const HierarchyFieldBuilder = () => {
  const dispatch = useDispatch();
  const selectedHierarchy = useAppSelector(selectSelectedHierarchy);
  const formHierarchyTypes = useAppSelector(selectHierarchyTypes);

  const { data: hierarchyTypes } = useHierarchyTypesQuery({ archived: 'false' });
  const { data: locales } = useGetLocalesQuery();

  const localeIdsRef = useRef(locales);
  localeIdsRef.current = locales;

  const { reset, watch } = useForm<FormValues>({
    defaultValues: {},
  });

  const mergedFields = useMemo(() => {
    if (!hierarchyTypes || !formHierarchyTypes || !localeIdsRef.current) {
      return [];
    }

    return mergeHierarchyFields(hierarchyTypes, formHierarchyTypes, localeIdsRef.current);
  }, [hierarchyTypes, formHierarchyTypes]);

  const getFormValues = (fields: typeof mergedFields): FormValues =>
    fields.reduce(
      (acc, hf) => ({
        ...acc,
        [hf.id]: {
          visible: hf.visible,
          mandatoryTypeConfig: hf.mandatoryTypeConfig,
        },
      }),
      {} as FormValues
    );

  useEffect(() => {
    if (mergedFields.length > 0) {
      reset(getFormValues(mergedFields));
    }
  }, [mergedFields, reset]);

  const checkboxValueConverter = (value: unknown) => {
    const convertedValue = forceAssert<CheckboxChangeEventType>(value);
    return convertedValue.checked;
  };

  const onFieldClick = (hf: FormBuilerHierarchyType) => {
    dispatch(setSelectedHierarchy(hf));
  };

  const handleVisibilityChange = (hierarchyField: FormBuilerHierarchyType, fieldName: string, checked?: boolean) => {
    dispatch(
      updateHierarchyFieldData({
        ...hierarchyField,
        [fieldName]: checked,
      })
    );
  };

  return (
    <div className="flex max-h-full w-full flex-col overflow-auto p-2">
      {/* Header row */}
      <div className="mb-4 grid grid-cols-[minmax(7.5rem,1fr)_5rem_5rem] items-center gap-2">
        <div aria-label="Field names column">
          <span className="sr-only">Field names</span>
        </div>
        <span className="truncate text-center text-sm font-medium">Visible</span>
        <span className="truncate text-center text-sm font-medium">Mandatory</span>
      </div>

      {/* Field rows */}
      {mergedFields.length > 0 &&
        mergedFields?.map((hf) => {
          const fieldClasses = clsx(
            'w-full min-w-[7.5rem] mb-2 p-1 [&_*]:pointer-events-none rounded outline-primary-2 outline-2 hover:opacity-50 hover:outline',
            {
              'opacity-50 outline': (selectedHierarchy?.id ?? '') === hf.id,
            }
          );

          const isVisible = watch(`${hf.id}.visible`);

          return (
            <div key={hf.id} className="mb-2 grid grid-cols-[minmax(7.5rem,1fr)_5rem_5rem] items-center gap-2">
              <div className={fieldClasses} onClick={() => onFieldClick(hf)}>
                <Input name={`${hf.id}.caption`} label={hf.caption?.translations?.[0]?.value} disabled />
              </div>
              <span className="flex w-20 flex-shrink-0 justify-center pt-3">
                <Checkbox
                  initialChecked={hf.visible}
                  name="visible"
                  label=""
                  className="block w-full"
                  fieldValueConverter={checkboxValueConverter}
                  onChange={(checked) => handleVisibilityChange(hf, 'visible', checked?.checked)}
                />
              </span>
              {/*w-20 is equal to 5rem which matches the grid column definition*/}
              <span
                className={clsx('flex w-20 flex-shrink-0 justify-center pt-3', {
                  'cursor-not-allowed': !isVisible,
                })}
              >
                <Checkbox
                  initialChecked={hf.mandatoryTypeConfig}
                  name="mandatoryTypeConfig"
                  label=""
                  fieldValueConverter={checkboxValueConverter}
                  disabled={!isVisible}
                  onChange={(checked) => handleVisibilityChange(hf, 'mandatoryTypeConfig', checked?.checked)}
                />
              </span>
            </div>
          );
        })}
    </div>
  );
};

export default HierarchyFieldBuilder;
